/**
 * Export Flood MIS District Datewise Report to Excel
 * Date: 2026-01-21
 * Following Heatwave Pattern
 */

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  formatDateForMISExcel,
  getTimestamp,
  transformMISDistrictDatewiseData,
  transformMISDistrictData,
  transformMISStateDatewiseData,
  transformMISStateData
} from './excelUtilsFlood';

// NEW: Apply Flood Form1 Status Coloring (Cold Wave Pattern)
// Red = ALL last 4 columns = 0 (no reporting), Yellow = ANY > 0 (partial reporting)
const applyFloodForm1Formatting = (worksheet, columns) => {
  console.log('Applying Flood Form1 coloring...');

  // Find last 4 column indices: Affected, Partially Affected, Not Affected, Total Marked Blocks
  const last4Cols = ['Affected Blocks', 'Partially Affected Blocks', 'Not Affected Blocks', 'Total Marked Blocks'];
  const colIndices = last4Cols.map(colName => {
    const col = columns.find(c => c.label === colName);
    return col ? columns.indexOf(col) + 1 : -1; // ExcelJS 1-based indexing
  }).filter(idx => idx > 0);

  console.log('Last 4 columns indices:', colIndices);

  // Apply to data rows only (skip header row 1)
  worksheet.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return; // Skip header

    // Check if ALL last 4 columns = 0
    let allZero = true;
    let hasNonZero = false;

    colIndices.forEach(colIdx => {
      const cellValue = row.getCell(colIdx).value;
      const numValue = parseFloat(cellValue);
      if (!isNaN(numValue) && numValue > 0) {
        hasNonZero = true;
        allZero = false;
      }
    });

    // Apply row coloring
    row.eachCell({ includeEmpty: true }, (cell) => {
      if (allZero) {
        // RED: No reporting (all last 4 = 0)
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF0000' } // Red
        };
      } else if (hasNonZero) {
        // YELLOW: Partial reporting (any last 4 > 0)
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFFFF00' } // Yellow
        };
      }
    });
  });
};

// 🚀 PRODUCTION: Flood Form1 Excel - Matches Cold Wave EXACTLY
export const generateFloodForm1Excel = async (reportData) => {
  const { data, columnOrder, toolName, date, filename } = reportData;
  console.log('🚀 generateFloodForm1Excel START', { rows: data?.length, cols: columnOrder?.length });

  try {
    // ✅ STATIC imports - No dynamic import issues
    // const ExcelJS = await import('exceljs');
    // const { saveAs } = await import('file-saver');  // ✅ CORRECT file-saver import
    const { transformFloodForm1DataForExcel } = await import('./excelUtilsFlood.js');

    // Transform data
    const [columns, transformedData] = transformFloodForm1DataForExcel(data, columnOrder);
    if (!transformedData.length) throw new Error('No data to export');

    console.log('✅ Data transformed:', transformedData.length, 'rows');

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Flood Form1 Status');

    // Headers (Row 1)
    const headerRow = worksheet.addRow(columns.map(col => col.label));
    headerRow.font = { bold: true };
    headerRow.alignment = { horizontal: 'center', vertical: 'middle' };

    // Data rows (Row 2+)
    transformedData.forEach(row => {
      const rowData = columns.map(col => row[col.label] ?? 0);
      worksheet.addRow(rowData);
    });

    // ✅ Apply PERFECT Red/Yellow coloring (Cold Wave logic)
    applyFloodForm1Formatting(worksheet, columns);

    // Auto-fit columns
    columns.forEach((col, idx) => {
      const colLetter = String.fromCharCode(65 + idx);
      worksheet.getColumn(colLetter).width = Math.max(col.label.length + 2, 12);
    });

    // ✅ PERFECT filename matching reference
    const timestamp = getTimestamp();
    const finalFilename = `DailyFloodForm1Status_${timestamp}.xlsx`;

    // ✅ Download - BULLETPROOF
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, finalFilename);  // ✅ Now works!

    console.log('✅ SUCCESS:', finalFilename);
  } catch (error) {
    console.error('❌ Flood Form1 FAILED:', error);
    alert(`Excel Error: ${error.message}`);
    throw error;
  }
};

const formatMetadataDate = (dateValue) => {
  if (!dateValue) return '';
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateValue)) return dateValue;  // Already DD-MM-YYYY

  const date = new Date(dateValue);
  if (!isNaN(date.getTime())) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }
  return dateValue;
};

/**
 * Generate MIS Flood District Datewise Excel Report
 */
export const generateMISDistrictDatewiseExcel = async (reportData) => {
  const { data, fromDate, toDate } = reportData;

  console.log('📊 MIS Flood District Datewise Excel Generation');
  console.log('📊 Data structure:', {
    hasMisData: !!data?.mis_data,
    hasCumulativeData: !!data?.cumulative_data,
    dailyRecords: data?.mis_data?.mis_flood_report_district_date?.length || 0,
    cumulativeRecords: data?.cumulative_data?.mis_flood_report_district_date?.length || 0
  });

  try {
    // Transform data
    const { columns, transformedData } = transformMISDistrictDatewiseData(data);

    if (!columns || columns.length === 0) {
      console.error('❌ No columns returned');
      alert('No data available to export.');
      return;
    }

    if (!transformedData || transformedData.length === 0) {
      console.error('❌ No transformed data returned');
      alert('No data available to export.');
      return;
    }

    console.log(`✅ Transform successful: ${transformedData.length} rows, ${columns.length} columns`);

    // Create Excel workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    // Format dates
    const formattedFromDate = formatMetadataDate(fromDate);
    const formattedToDate =  formatMetadataDate(toDate);

    // Row 1: Report Name
    const metadataRow1 = worksheet.addRow(['Report Name: DIST_DATE_REPORT']);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(1, 1, 1, columns.length + 1);

    // Row 2: From Date
    const metadataRow2 = worksheet.addRow([`From Date: ${formattedFromDate}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(2, 1, 2, columns.length + 1);

    // Row 3: To Date
    const metadataRow3 = worksheet.addRow([`To Date: ${formattedToDate}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(3, 1, 3, columns.length + 1);

    // Row 4: Headers (first column blank, then all header labels)
    const headerRow = worksheet.addRow(['', ...columns.map(col => col.label)]);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber === 1 || colNumber === 2) {
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      }
      cell.font = { bold: true, color: { argb: 'FF000000' } };
    });

    // Data rows (Rows 5+) - including TOTAL row at the end
    transformedData.forEach((row, index) => {
      const isLastRow = (index === transformedData.length - 1);
      const firstColumnValue = isLastRow ? 'TOTAL' : 'NA';

      // Build row data from columns
      const rowData = columns.map(col => {
        const value = row[col.label];

        if (value !== undefined && value !== null) {
          // Convert string numbers to actual numbers for TOTAL row
          if (isLastRow && typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
            return parseFloat(value);  // ✅ CONVERT TO NUMERIC
          }
          return value;
        }
        return '-';
      });

      // Add first column + data
      const dataRow = worksheet.addRow([firstColumnValue, ...rowData]);
      dataRow.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
      });
    });

    // Set column widths to exactly 1.70 cm
    // Correct formula: (cm * 37.795 pixels/cm) / 8.43 pixels/character
    const columnWidthInCm = 1.70;
    const pixelsPerCm = 37.795; // Standard 96 DPI
    const pixelsPerChar = 8.43;  // Excel default (Calibri 11)
    const excelWidthUnit = (columnWidthInCm * pixelsPerCm) / pixelsPerChar;

    for (let i = 1; i <= columns.length + 1; i++) {
      worksheet.getColumn(i).width = 8.65; // ≈ 7.62 units
      //worksheet.getColumn(i).width = excelWidthUnit; // ≈ 7.62 units
    }

    // Set row heights
    worksheet.getRow(1).height = 15;
    worksheet.getRow(2).height = 15;
    worksheet.getRow(3).height = 15;
    worksheet.getRow(4).height = 15;

    // Generate filename
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
    const finalFilename = `MIS_Dist_Datewise_Flood_Report_${dateStr}_${timeStr}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, finalFilename);

    console.log(`✅ Generated: ${finalFilename}`);
  } catch (error) {
    console.error('❌ MIS Excel error:', error);
    alert(`Excel generation failed: ${error.message}`);
  }
};

/**
 * Generate MIS Flood District Excel Report (NO date column)
 */
export const generateMISDistrictExcel = async (reportData) => {
  const { data } = reportData;

  console.log('📊 MIS Flood District Excel Generation');
  console.log('📊 Data structure:', {
    hasData: !!data,
    recordCount: data?.data?.length || data?.length || 0
  });

  try {
    // Transform data
    const { columns, transformedData } = transformMISDistrictData(data);

    if (!columns || columns.length === 0) {
      console.error('❌ No columns returned');
      alert('No data available to export.');
      return;
    }

    if (!transformedData || transformedData.length === 0) {
      console.error('❌ No transformed data returned');
      alert('No data available to export.');
      return;
    }

    console.log(`✅ Transform successful: ${transformedData.length} rows, ${columns.length} columns`);

    // Create Excel workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    // Row 1: Report Name
    const metadataRow1 = worksheet.addRow(['Report Name: DIST_REPORT']);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(1, 1, 1, columns.length + 1);

    // Row 2: From Date
    const { fromDate, toDate } = reportData;
    const formattedFromDate = formatMetadataDate(fromDate);
    const metadataRow2 = worksheet.addRow([`From Date: ${formattedFromDate}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(2, 1, 2, columns.length + 1);

    // Row 3: To Date
    const formattedToDate = formatMetadataDate(toDate);
    const metadataRow3 = worksheet.addRow([`To Date: ${formattedToDate}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(3, 1, 3, columns.length + 1);

    // Row 4: Headers (first column blank, then all header labels)
    const headerRow = worksheet.addRow(['', ...columns.map(col => col.label)]);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber === 1 || colNumber === 2) {
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      }
      cell.font = { bold: true, color: { argb: 'FF000000' } };
    });

    // Data rows (Rows 3+) - including TOTAL row at the end
    transformedData.forEach((row, index) => {
      const isLastRow = (index === transformedData.length - 1);
      const firstColumnValue = isLastRow ? 'TOTAL' : 'NA';

      // Build row data from columns
      const rowData = columns.map(col => {
        const value = row[col.label];
        return value !== undefined ? value : '-';
      });

      // Add first column + data
      const dataRow = worksheet.addRow([firstColumnValue, ...rowData]);
      dataRow.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
      });
    });

    // Set column widths to 1.70 cm
    for (let i = 1; i <= columns.length + 1; i++) {
      worksheet.getColumn(i).width = 8.65;
    }

    // Set row heights
    worksheet.getRow(1).height = 15;
    worksheet.getRow(2).height = 15;
    worksheet.getRow(3).height = 15;
    worksheet.getRow(4).height = 15;

    // Generate filename
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
    const finalFilename = `MIS_Dist_Flood_Report_${dateStr}_${timeStr}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });
    saveAs(blob, finalFilename);

    console.log(`✅ Generated: ${finalFilename}`);
  } catch (error) {
    console.error('❌ MIS Excel error:', error);
    alert(`Excel generation failed: ${error.message}`);
  }
};

/**
 * Generate MIS Flood State Datewise Excel Report
 */
export const generateMISStateDatewiseExcel = async (reportData) => {
  const { data, fromDate, toDate } = reportData;

  console.log('📊 MIS Flood State Datewise Excel Generation');
  console.log('📊 Data structure:', {
    hasMisData: !!data?.mis_data,
    hasCumulativeData: !!data?.cumulative_data,
    dailyRecords: data?.mis_data?.mis_flood_report_state_date?.length || 0,
    cumulativeRecords: data?.cumulative_data?.mis_flood_report_state_date?.length || 0
  });

  try {
    // Transform data
    const { columns, transformedData } = transformMISStateDatewiseData(data);

    if (!columns || columns.length === 0) {
      console.error('❌ No columns returned');
      alert('No data available to export.');
      return;
    }

    if (!transformedData || transformedData.length === 0) {
      console.error('❌ No transformed data returned');
      alert('No data available to export.');
      return;
    }

    console.log(`✅ Transform successful: ${transformedData.length} rows, ${columns.length} columns`);

    // Create Excel workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    // Format dates
    const formattedFromDate = formatMetadataDate(fromDate);
    const formattedToDate = formatMetadataDate(toDate);

    // Row 1: Report Name
    const metadataRow1 = worksheet.addRow(['Report Name: STATE_DATE_REPORT']);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(1, 1, 1, columns.length + 1);

    // Row 2: From Date
    const metadataRow2 = worksheet.addRow([`From Date: ${formattedFromDate}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(2, 1, 2, columns.length + 1);

    // Row 3: To Date
    const metadataRow3 = worksheet.addRow([`To Date: ${formattedToDate}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(3, 1, 3, columns.length + 1);

    // Row 4: Headers (first column blank, then all header labels)
    const headerRow = worksheet.addRow([...columns.map(col => col.label)]);
    headerRow.eachCell((cell, colNumber) => {
      if (colNumber === 1 || colNumber === 2) {
        cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      } else {
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      }
      cell.font = { bold: true, color: { argb: 'FF000000' } };
    });

    // Data rows (Rows 5+) - including TOTAL row at the end
    transformedData.forEach((row, index) => {
      const isLastRow = (index === transformedData.length - 1);
      const firstColumnValue = isLastRow ? 'TOTAL' : 'NA';

      // Build row data from columns
      const rowData = columns.map(col => {
        const value = row[col.label];

        if (value !== undefined && value !== null) {
          // Convert string numbers to actual numbers for TOTAL row
          if (isLastRow && typeof value === 'string' && /^\d+(\.\d+)?$/.test(value)) {
            return parseFloat(value); // ✅ CONVERT TO NUMERIC
          }

          return value;
        }

        return '-';
      });

      // Add first column + data
      const dataRow = worksheet.addRow([...rowData]);

      dataRow.eachCell((cell, colNumber) => {
        if (colNumber === 1 || colNumber === 2) {
          cell.alignment = { horizontal: 'left', vertical: 'middle' };
        } else {
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }

        cell.border = {
          top: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          left: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          bottom: { style: 'thin', color: { argb: 'FFD3D3D3' } },
          right: { style: 'thin', color: { argb: 'FFD3D3D3' } }
        };
      });
    });

    // Set column widths to exactly 1.70 cm
    for (let i = 1; i <= columns.length + 1; i++) {
      worksheet.getColumn(i).width = 8.65; // ≈ 1.70 cm
    }

    // Set row heights
    worksheet.getRow(1).height = 15;
    worksheet.getRow(2).height = 15;
    worksheet.getRow(3).height = 15;
    worksheet.getRow(4).height = 15;

    // Generate filename
    const now = new Date();
    const dateStr = now.toISOString().split('T')[0];
    const timeStr = now.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
    const finalFilename = `MIS_State_Datewise_Flood_Report_${dateStr}_${timeStr}.xlsx`;

    // Save file
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    saveAs(blob, finalFilename);
    console.log(`✅ Generated: ${finalFilename}`);

  } catch (error) {
    console.error('❌ MIS State Datewise Excel error:', error);
    alert(`Excel generation failed: ${error.message}`);
  }
};

/**
 * Generate MIS State Flood Report Excel (NO date column)
 * Report Name: STATE_REPORT
 * Structure: District Name + 122 data columns
 */
export const generateMISStateExcel = async (reportData) => {
  console.log('📊 generateMISStateExcel called');

  // Extract data and parameters
  const { data, fromDate, toDate } = reportData;

  console.log('Data structure:', {
    hasData: !!data,
    recordCount: data?.data?.length || data?.length || 0
  });

  // Transform data
  const { columns, transformedData } = transformMISStateData(data);

  if (!columns.length || !transformedData.length) {
    throw new Error('No data available for State Flood Report');
  }

  console.log(`✅ Columns: ${columns.length}, Rows: ${transformedData.length}`);

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('MIS State Flood Report');

  // Row 1: Report Name
  const metadataRow1 = worksheet.addRow(['Report Name: STATE_REPORT']);
  metadataRow1.getCell(1).font = { bold: true };
  metadataRow1.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(1, 1, 1, columns.length);

  // Row 2: From Date
  const formattedFromDate = formatMetadataDate(fromDate);
  const metadataRow2 = worksheet.addRow([`From Date: ${formattedFromDate}`]);
  metadataRow2.getCell(1).font = { bold: true };
  metadataRow2.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(2, 1, 2, columns.length);

  // Row 3: To Date
  const formattedToDate = formatMetadataDate(toDate);
  const metadataRow3 = worksheet.addRow([`To Date: ${formattedToDate}`]);
  metadataRow3.getCell(1).font = { bold: true };
  metadataRow3.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
  worksheet.mergeCells(3, 1, 3, columns.length);

  // Row 4: Headers
  const headerRow = worksheet.addRow([...columns.map(col => col.label)]);
  headerRow.font = { bold: true };

  // Rows 5+: Data rows
  transformedData.forEach(row => {
    const rowData = columns.map(col => {
      const value = row[col.label];
      return (value !== null && value !== undefined) ? value : '';
    });
    const dataRow = worksheet.addRow([...rowData]);
    // Right-align all data cells (like TOTAL row)
    dataRow.eachCell((cell, colNumber) => {
      if (colNumber === 1) {
        cell.alignment = { horizontal: 'right', vertical: 'middle' };
      } else {
        cell.alignment = { horizontal: 'left', vertical: 'middle' };
      }
    });
  });

  for (let i = 1; i <= columns.length; i++) {
    worksheet.getColumn(i).width = 8.65; // 8.65 = 7.62 units
  }

  // Generate filename with timestamp
  const timestamp = getTimestamp();
  const filename = `MIS_State_Flood_Report_${timestamp}.xlsx`;

  // Write to buffer
  const buffer = await workbook.xlsx.writeBuffer();

  console.log(`✅ Excel generated: ${filename}`);

  // Save file
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  });
  saveAs(blob, filename);

  console.log(`✅ File saved: ${filename}`);
};

/**
 * ✅ ROUTER FUNCTION - Routes to appropriate generator based on selectedView
 * This is what Chatbot.jsx imports as "generateReportExcel"
 */
export const generateReportExcel = async (params) => {
  console.log('🔀 Flood MIS Excel Router called');
  console.log('📊 Params:', {
    selectedView: params.selectedView,
    toolName: params.toolName,
    hasData: !!params.data
  });

  const { selectedView } = params;

  // Route based on selectedView
  switch (selectedView) {
    case 'dist_date':
    case 'distdate':
      console.log('✅ Routing to District Datewise Excel');
      await generateMISDistrictDatewiseExcel(params);
      break;

    case 'dist':
      console.log('✅ Routing to District Excel');
      await generateMISDistrictExcel(params);
      break;

    case 'state':
      console.log('✅ Routing to State Excel');
      await generateMISStateExcel(params);
      break;

    case 'state_date':
    case 'statedate':
      console.log('✅ Routing to State Datewise Excel');
      await generateMISStateDatewiseExcel(params);
      break;


    default:
      console.error('❌ Unknown selectedView:', selectedView);
      alert(`Unknown report type: ${selectedView}`);
  }
};

export default {
  generateMISDistrictDatewiseExcel,
  generateMISDistrictExcel,
  generateMISStateDatewiseExcel,
  generateMISStateExcel,
  generateReportExcel
};

